import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { uploadSignedAsset } from '@/api/misc';
import { addApplicationFile, signApplicationFile } from '@/api/Rover/ApplicationForm';
import type { IGetApplicationServiceResDataFile } from '@/api/Rover/ApplicationForm/types';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import {
  type CriticalRepairDocumentField,
  type CriticalRepairDocuments,
  criticalRepairDocumentFields,
} from '@/features/ApplicationForm/FormConfig/CR2025/CRForm/fileMetadata';
import { notifyUser } from '@/store/Toast';
import DocumentsDetailsUI, { criticalRepairDocumentLabels, type DisplayApplicationFile } from './DocumentsDetailsUI';

interface DocumentsDetailsProps {
  documents?: CriticalRepairDocuments;
  files?: IGetApplicationServiceResDataFile[];
  canUploadFiles?: boolean;
}

interface UploadVariables {
  field: CriticalRepairDocumentField;
  file: File;
}

function groupFilesByField(
  documents: CriticalRepairDocuments | undefined,
  files: IGetApplicationServiceResDataFile[]
): Record<CriticalRepairDocumentField, DisplayApplicationFile[]> {
  return criticalRepairDocumentFields.reduce(
    (grouped, field) => {
      const persistedFiles = files.filter((file) => file.field === field);
      const persistedKeys = new Set(persistedFiles.map((file) => file.key));
      const legacyFiles = (documents?.[field] ?? []).flatMap((value) => {
        if (!value || typeof value !== 'object') return [];
        const file = value as Partial<DisplayApplicationFile>;
        if (!file.key || persistedKeys.has(file.key)) return [];
        return [file as DisplayApplicationFile];
      });

      grouped[field] = [...persistedFiles, ...legacyFiles];
      return grouped;
    },
    {} as Record<CriticalRepairDocumentField, DisplayApplicationFile[]>
  );
}

export default function DocumentsDetails({ documents, files = [], canUploadFiles = false }: DocumentsDetailsProps) {
  const { applicationId = '' } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const groupedFiles = groupFilesByField(documents, files);

  const uploadFile = useMutation({
    mutationFn: async ({ field, file }: UploadVariables) => {
      const sign = await signApplicationFile({
        applicationId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });
      await uploadSignedAsset({ file, url: sign.url });
      await addApplicationFile({
        applicationId,
        files: [
          {
            name: file.name,
            path: sign.path,
            key: sign.key,
            type: file.type,
            field,
            description: criticalRepairDocumentLabels[field],
          },
        ],
      });
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [RoverReactQueryKey.GET_APPLICATION_DETAILS, applicationId],
      });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${variables.file.name} uploaded successfully.`,
          toastType: 'success',
        })
      );
    },
    onError: (_, variables) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Failed to upload ${variables.file.name}. Select the file again to retry.`,
          toastType: 'failed',
        })
      );
    },
  });

  const handleUpload = (field: CriticalRepairDocumentField, file: File) => {
    uploadFile.mutate({ field, file });
  };

  return (
    <DocumentsDetailsUI
      groupedFiles={groupedFiles}
      canUploadFiles={canUploadFiles && Boolean(applicationId)}
      isUploading={uploadFile.isLoading}
      uploadingField={uploadFile.variables?.field}
      onUpload={handleUpload}
    />
  );
}
